import { ConversationStatus } from '@provision/conversation-types';
import { UrlParams } from '@provision/portal-routes';
import { Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormAutocomplete } from 'src/components/Forms/FormAutocomplete/form-autocomplete';
import { AutoCompleteOption } from 'src/components/Forms/types';
import { Conversation } from 'src/graphql';
import { useSearchConversations } from 'src/graphql/entities/search/search-conversation';
import { useSelectedConversation } from 'src/pages/Conversations';
import { useRouteParams } from 'src/utils/routing';
import {
  ContactSupportFormField,
  ContactSupportFormValues,
} from './contact-support-form.types';

interface SearchConversationFieldProps {
  required: boolean | string;
}
// LinkConversationData is lightweight type that includes minimal required fields for this component.
// It is needed to avoid using Conversation type in conjunction with `useForm` as it fails to properly
// resolve types and TypeScript complains on "excessively deep and possibly infinite" type instantiation.
export type LinkConversationData = Pick<
  Conversation,
  'friendlyId' | 'subject' | 'conversationId' | 'partnerId'
>;

export type LinkConversationOption = AutoCompleteOption & LinkConversationData;

const toLinkConversationOption = ({
  friendlyId,
  subject,
  conversationId,
  partnerId,
}: LinkConversationData): LinkConversationOption => ({
  id: conversationId,
  label: `${friendlyId} | ${subject}`,
  friendlyId,
  subject,
  conversationId,
  partnerId,
});

export const SearchConversationField = ({ required }: SearchConversationFieldProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const { setValue } = useFormContext<ContactSupportFormValues>();

  const { recordId: conversationId } = useRouteParams(UrlParams.recordId);

  const { selectedConversation } = useSelectedConversation({
    conversationId: conversationId || '',
  });

  useEffect(() => {
    if (selectedConversation) {
      setValue(
        ContactSupportFormField.LinkedConversation,
        toLinkConversationOption(selectedConversation),
      );
    }
  }, [setValue, selectedConversation]);

  const searchConversationsFilter = useMemo(
    () => [
      {
        attribute: 'conversationStatusId',
        operator: '!=',
        value: ConversationStatus.Closed,
      },
    ],
    [],
  );

  const { records, loadMore, isLoading, hasMore } = useSearchConversations({
    searchTerm,
    filter: searchConversationsFilter,
    sort: {
      attribute: 'subject',
    },
  });

  const options = useMemo(() => records.map(toLinkConversationOption), [records]);

  return (
    <FormAutocomplete<LinkConversationOption>
      type="autocomplete"
      name={ContactSupportFormField.LinkedConversation}
      label="Linked Conversation"
      options={options}
      loading={isLoading}
      required={required}
      showErrorText
      data-testid="contact-support-linked-conversation"
      placeholder="Search & Select Conversation"
      icon={<Search aria-hidden />}
      onSearchChange={setSearchTerm}
      hasMore={hasMore}
      onLoadMore={loadMore}
    />
  );
};
